// src/pages/Campaigns/VolunteerManagement/VolunteerManagement.js

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, FormControl, InputLabel, Select, MenuItem, Grid,
  Alert, Pagination, CircularProgress, Stack, Card, CardContent,
  InputAdornment, Snackbar, Checkbox, Menu, Tooltip, Divider
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Upload as UploadIcon,
  Search as SearchIcon, Person as PersonIcon, Event as EventIcon, Code as CodeIcon,
  CheckCircle as CheckCircleIcon, Cancel as CancelIcon, MoreVert as MoreVertIcon,
  Check as CheckIcon, Block as BlockIcon, DeleteForever as DeleteForeverIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';

const VolunteerManagement = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Selection state for bulk actions
  const [selectedVolunteers, setSelectedVolunteers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Menu state for actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentVolunteer, setCurrentVolunteer] = useState(null);
  
  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);
  
  // Modals
  const [volunteerDialog, setVolunteerDialog] = useState({ open: false, volunteer: null });
  const [uploadDialog, setUploadDialog] = useState(false);
  const [embedDialog, setEmbedDialog] = useState(false);
  const [hardDeleteDialog, setHardDeleteDialog] = useState({ open: false, volunteer: null });
  const [uploadFile, setUploadFile] = useState(null);
  
  // Embed settings
  const [embedSettings, setEmbedSettings] = useState({
    type: 'iframe',
    width: '100%',
    height: '800px'
  });
  const [embedUrl, setEmbedUrl] = useState('');
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Form data
  const [formData, setFormData] = useState({
    full_name: '',
    phone_number: '',
    email: '',
    age: '',
    city: '',
    profession: '',
    preferences: {
      availability_type: '',
      preferred_time_slot: '',
      location: '',
      event_type: '',
      notes: ''
    }
  });

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const viewPerm = await hasPermission('volunteer_management', 'view');
        const modifyPerm = await hasPermission('volunteer_management', 'add|edit');
        const deletePerm = await hasPermission('volunteer_management', 'delete');
        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  // Load volunteers
  useEffect(() => {
    if (canView && chatbotId) {
      const timeoutId = setTimeout(() => {
        loadVolunteers();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [canView, chatbotId, page, statusFilter, searchTerm]);

  // Load embed settings
  useEffect(() => {
    if (canView && chatbotId) {
      loadEmbedSettings();
    }
  }, [canView, chatbotId]);

  const loadVolunteers = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/volunteers`, {
        params: {
          chatbot_id: chatbotId,
          page,
          per_page: 10,
          status: statusFilter,
          search: searchTerm
        }
      });
      setVolunteers(response.data.data);
      setTotalPages(response.data.pagination.pages);
      setSelectedVolunteers([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Error loading volunteers:', error);
      showSnackbar('Error loading volunteers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadEmbedSettings = async () => {
    try {
      const response = await axiosInstance.get(`/api/volunteers/embed-settings`, {
        params: { chatbot_id: chatbotId }
      });
      
      if (response.data.success) {
        setEmbedUrl(response.data.data.form_url);
      }
    } catch (error) {
      console.error('Error loading embed settings:', error);
    }
  };

  // Handle status change
  const handleStatusChange = async (volunteerId, newStatus) => {
    try {
      await axiosInstance.put(`/api/volunteers/status`, 
        { status: newStatus },
        {
          params: {
            volunteer_id: volunteerId,
            chatbot_id: chatbotId
          }
        }
      );
      
      const statusMessage = newStatus === 'INACTIVE' ? 'deactivated' : newStatus.toLowerCase();
      showSnackbar(`Volunteer ${statusMessage} successfully`, 'success');
      loadVolunteers();
      handleCloseMenu();
    } catch (error) {
      console.error('Error updating status:', error);
      showSnackbar('Error updating volunteer status', 'error');
    }
  };

  // Handle bulk approve
  const handleBulkApprove = async () => {
    if (selectedVolunteers.length === 0) {
      showSnackbar('Please select volunteers to approve', 'warning');
      return;
    }

    if (!window.confirm(`Approve ${selectedVolunteers.length} volunteer(s)?`)) return;

    try {
      const response = await axiosInstance.post(`/api/volunteers/bulk-approve`, 
        { volunteer_ids: selectedVolunteers },
        { params: { chatbot_id: chatbotId } }
      );
      
      showSnackbar(response.data.message, 'success');
      setSelectedVolunteers([]);
      setSelectAll(false);
      loadVolunteers();
    } catch (error) {
      console.error('Error bulk approving:', error);
      showSnackbar('Error approving volunteers', 'error');
    }
  };

  // Handle selection
  const handleSelectVolunteer = (volunteerId) => {
    setSelectedVolunteers(prev => {
      if (prev.includes(volunteerId)) {
        return prev.filter(id => id !== volunteerId);
      } else {
        return [...prev, volunteerId];
      }
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedVolunteers([]);
    } else {
      const pendingVolunteerIds = volunteers
        .filter(v => v.status === 'PENDING')
        .map(v => v.id);
      setSelectedVolunteers(pendingVolunteerIds);
    }
    setSelectAll(!selectAll);
  };

  // Menu handlers
  const handleOpenMenu = (event, volunteer) => {
    setAnchorEl(event.currentTarget);
    setCurrentVolunteer(volunteer);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setCurrentVolunteer(null);
  };

  const handleSaveVolunteer = async () => {
    // Validate required fields
    if (!formData.full_name || !formData.phone_number || !formData.email) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showSnackbar('Please enter a valid email address', 'error');
      return;
    }

    try {
      if (volunteerDialog.volunteer) {
        // Update
        await axiosInstance.put(`/api/volunteers`, formData, {
          params: {
            volunteer_id: volunteerDialog.volunteer.id,
            chatbot_id: chatbotId
          }
        });
        showSnackbar('Volunteer updated successfully', 'success');
      } else {
        // Create
        await axiosInstance.post(`/api/volunteers`, formData, {
          params: { chatbot_id: chatbotId }
        });
        showSnackbar('Volunteer created successfully', 'success');
      }
      
      handleCloseDialog();
      loadVolunteers();
    } catch (error) {
      console.error('Error saving volunteer:', error);
      const errorMessage = error.response?.data?.message || 'Error saving volunteer. Please try again.';
      showSnackbar(errorMessage, 'error');
    }
  };

  // Soft delete (deactivate)
  const handleDeactivateVolunteer = async (volunteerId) => {
    if (!window.confirm('Are you sure you want to deactivate this volunteer? They can be reactivated later.')) return;
    
    try {
      await axiosInstance.delete(`/api/volunteers`, {
        params: {
          volunteer_id: volunteerId,
          chatbot_id: chatbotId
        }
      });
      showSnackbar('Volunteer deactivated successfully', 'success');
      loadVolunteers();
      handleCloseMenu();
    } catch (error) {
      console.error('Error deactivating volunteer:', error);
      showSnackbar('Error deactivating volunteer', 'error');
    }
  };

  // Hard delete (permanent)
  const handleHardDeleteVolunteer = async () => {
    const volunteer = hardDeleteDialog.volunteer;
    if (!volunteer) return;

    try {
      await axiosInstance.delete(`/api/volunteers/hard-delete`, {
        params: {
          volunteer_id: volunteer.id,
          chatbot_id: chatbotId
        }
      });
      showSnackbar('Volunteer permanently deleted', 'success');
      setHardDeleteDialog({ open: false, volunteer: null });
      loadVolunteers();
      handleCloseMenu();
    } catch (error) {
      console.error('Error permanently deleting volunteer:', error);
      showSnackbar('Error deleting volunteer', 'error');
    }
  };

  const handleUploadCSV = async () => {
    if (!uploadFile) {
      showSnackbar('Please select a file', 'error');
      return;
    }
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', uploadFile);
    
    try {
      setLoading(true);
      const response = await axiosInstance.post(`/api/volunteers/upload-csv`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { chatbot_id: chatbotId }
      });
      
      showSnackbar(response.data.message, 'success');
      setUploadDialog(false);
      setUploadFile(null);
      loadVolunteers();
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMessage = error.response?.data?.message || 'Error uploading file';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (volunteer = null) => {
    if (volunteer) {
      setFormData({
        full_name: volunteer.full_name,
        phone_number: volunteer.phone_number,
        email: volunteer.email,
        age: volunteer.age || '',
        city: volunteer.city || '',
        profession: volunteer.profession || '',
        preferences: {
          availability_type: volunteer.preferences?.[0]?.availability_type || '',
          preferred_time_slot: volunteer.preferences?.[0]?.preferred_time_slot || '',
          location: volunteer.preferences?.[0]?.location || '',
          event_type: volunteer.preferences?.[0]?.event_type || '',
          notes: volunteer.preferences?.[0]?.notes || ''
        }
      });
    } else {
      setFormData({
        full_name: '',
        phone_number: '',
        email: '',
        age: '',
        city: '',
        profession: '',
        preferences: {
          availability_type: '',
          preferred_time_slot: '',
          location: '',
          event_type: '',
          notes: ''
        }
      });
    }
    setVolunteerDialog({ open: true, volunteer });
  };

  const handleCloseDialog = () => {
    setVolunteerDialog({ open: false, volunteer: null });
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: 'success',
      INACTIVE: 'error',
      PENDING: 'warning'
    };
    return colors[status] || 'default';
  };

  const generateEmbedCode = () => {
    switch (embedSettings.type) {
      case 'iframe':
        return `<iframe src="${embedUrl}" width="${embedSettings.width}" height="${embedSettings.height}" frameborder="0"></iframe>`;
      case 'popup':
        return `<script>function openVolunteerForm(){window.open('${embedUrl}','volunteer','width=600,height=800,scrollbars=yes');}</script><button onclick="openVolunteerForm()">Become a Volunteer</button>`;
      case 'button':
        return `<a href="${embedUrl}" target="_blank" style="display:inline-block;padding:12px 24px;background:#1976d2;color:white;text-decoration:none;border-radius:4px;">Volunteer With Us</a>`;
      default:
        return '';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Copied to clipboard!');
  };

  // Count pending volunteers
  const pendingCount = volunteers.filter(v => v.status === 'PENDING').length;

  if (checkingPermissions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 6 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view volunteer management.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Volunteer Management
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {canModify && (
            <>
              <Button
                variant="outlined"
                startIcon={<EventIcon />}
                onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/events`)}
                size="small"
              >
                Create Event
              </Button>
              <Button
                variant="outlined"
                startIcon={<CodeIcon />}
                onClick={() => setEmbedDialog(true)}
                size="small"
              >
                Embed Form
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => setUploadDialog(true)}
                size="small"
              >
                Upload CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                size="small"
              >
                Add Volunteer
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {/* Pending Alert & Bulk Actions */}
      {pendingCount > 0 && canModify && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          action={
            selectedVolunteers.length > 0 && (
              <Button 
                color="inherit" 
                size="small" 
                startIcon={<CheckIcon />}
                onClick={handleBulkApprove}
              >
                Approve Selected ({selectedVolunteers.length})
              </Button>
            )
          }
        >
          You have <strong>{pendingCount}</strong> pending volunteer(s) awaiting approval.
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, or phone"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Volunteers Table */}
      <Paper sx={{ overflowX: 'auto' }}>
        <TableContainer sx={{ minWidth: { xs: 600, md: 'auto' } }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                {canModify && pendingCount > 0 && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      indeterminate={selectedVolunteers.length > 0 && !selectAll}
                    />
                  </TableCell>
                )}
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Profession</TableCell>
                <TableCell>Preference</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : volunteers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Box sx={{ py: 3 }}>
                      <PersonIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                      <Typography variant="body1" color="text.secondary">
                        No volunteers found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                volunteers.map((volunteer) => (
                  <TableRow key={volunteer.id} hover>
                    {canModify && pendingCount > 0 && (
                      <TableCell padding="checkbox">
                        {volunteer.status === 'PENDING' && (
                          <Checkbox
                            checked={selectedVolunteers.includes(volunteer.id)}
                            onChange={() => handleSelectVolunteer(volunteer.id)}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell>{volunteer.full_name}</TableCell>
                    <TableCell>{volunteer.email}</TableCell>
                    <TableCell>{volunteer.phone_number}</TableCell>
                    <TableCell>{volunteer.profession || '-'}</TableCell>
                    <TableCell>
                      {volunteer.preferences?.[0]?.availability_type ? (
                        <Chip 
                          label={volunteer.preferences[0].availability_type} 
                          size="small" 
                          variant="outlined"
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={volunteer.status}
                        color={getStatusColor(volunteer.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {canModify && volunteer.status === 'PENDING' && (
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleStatusChange(volunteer.id, 'ACTIVE')}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {canModify && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(volunteer)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenMenu(e, volunteer)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, newPage) => setPage(newPage)}
              color="primary"
              sx={{
                '& .Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: '#fff !important',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {currentVolunteer && canModify && (
          <>
            {currentVolunteer.status === 'PENDING' && (
              <MenuItem onClick={() => handleStatusChange(currentVolunteer.id, 'ACTIVE')}>
                <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} color="success" />
                Approve Volunteer
              </MenuItem>
            )}
            {currentVolunteer.status === 'ACTIVE' && (
              <MenuItem onClick={() => handleStatusChange(currentVolunteer.id, 'INACTIVE')}>
                <BlockIcon fontSize="small" sx={{ mr: 1 }} color="error" />
                Deactivate
              </MenuItem>
            )}
            {currentVolunteer.status === 'INACTIVE' && (
              <MenuItem onClick={() => handleStatusChange(currentVolunteer.id, 'ACTIVE')}>
                <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} color="success" />
                Activate
              </MenuItem>
            )}
            
            <Divider />
          </>
        )}
        
        {canDelete && currentVolunteer && currentVolunteer.status !== 'INACTIVE' && (
          <MenuItem onClick={() => handleDeactivateVolunteer(currentVolunteer.id)}>
            <CancelIcon fontSize="small" sx={{ mr: 1 }} color="warning" />
            Deactivate Volunteer
          </MenuItem>
        )}
        
        {canDelete && currentVolunteer && (
          <MenuItem 
            onClick={() => {
              handleCloseMenu();
              setHardDeleteDialog({ open: true, volunteer: currentVolunteer });
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteForeverIcon fontSize="small" sx={{ mr: 1 }} />
            Delete Permanently
          </MenuItem>
        )}
      </Menu>

      {/* Hard Delete Confirmation Dialog */}
      <Dialog
        open={hardDeleteDialog.open}
        onClose={() => setHardDeleteDialog({ open: false, volunteer: null })}
      >
        <DialogTitle>Permanently Delete Volunteer?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Warning!</strong> This action cannot be undone.
          </Alert>
          <Typography>
            Are you sure you want to permanently delete <strong>{hardDeleteDialog.volunteer?.full_name}</strong>? 
            All associated data will be removed from the system.
          </Typography>
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Consider deactivating instead to preserve data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHardDeleteDialog({ open: false, volunteer: null })}>
            Cancel
          </Button>
          <Button 
            onClick={handleHardDeleteVolunteer} 
            color="error" 
            variant="contained"
            startIcon={<DeleteForeverIcon />}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Volunteer Dialog (Add/Edit) */}
      <Dialog open={volunteerDialog.open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {volunteerDialog.volunteer ? 'Edit Volunteer' : 'Add New Volunteer'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} color="primary" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Full Name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Phone Number"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Age"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Enter city name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Profession"
                value={formData.profession}
                onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                placeholder="e.g., Teacher, Engineer, Student"
              />
            </Grid>

            {/* Preferences Section */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} color="primary" gutterBottom>
                Preferences
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Availability</InputLabel>
                <Select
                  value={formData.preferences.availability_type}
                  onChange={(e) => setFormData({
                    ...formData,
                    preferences: { ...formData.preferences, availability_type: e.target.value }
                  })}
                  label="Availability"
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="WEEKEND">Weekend</MenuItem>
                  <MenuItem value="WEEKDAY">Weekday</MenuItem>
                  <MenuItem value="BOTH">Both</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Preferred Timing</InputLabel>
                <Select
                  value={formData.preferences.preferred_time_slot}
                  onChange={(e) => setFormData({
                    ...formData,
                    preferences: { ...formData.preferences, preferred_time_slot: e.target.value }
                  })}
                  label="Preferred Timing"
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="MORNING_6_9">6-9 AM (Morning)</MenuItem>
                  <MenuItem value="AFTERNOON_1_3">1-3 PM (Afternoon)</MenuItem>
                  <MenuItem value="EVENING_6_8">6-8 PM (Evening)</MenuItem>
                  <MenuItem value="EVENING_6_9">6-9 PM</MenuItem>
                  <MenuItem value="FLEXIBLE">Flexible</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Preferred Location"
                value={formData.preferences.location}
                onChange={(e) => setFormData({
                  ...formData,
                  preferences: { ...formData.preferences, location: e.target.value }
                })}
                placeholder="City or area"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Event Type Interest</InputLabel>
                <Select
                  value={formData.preferences.event_type}
                  onChange={(e) => setFormData({
                    ...formData,
                    preferences: { ...formData.preferences, event_type: e.target.value }
                  })}
                  label="Event Type Interest"
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="RALLY">Rally</MenuItem>
                  <MenuItem value="DONATION">Donation</MenuItem>
                  <MenuItem value="FUNDRAISING">Fundraising</MenuItem>
                  <MenuItem value="COMMUNITY_SERVICE">Community Service</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional Notes"
                value={formData.preferences.notes}
                onChange={(e) => setFormData({
                  ...formData,
                  preferences: { ...formData.preferences, notes: e.target.value }
                })}
                placeholder="Any special skills or preferences..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveVolunteer}>
            {volunteerDialog.volunteer ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload CSV Dialog */}
      <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Volunteers CSV/Excel</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            File should contain columns: full_name, phone_number, email, age (optional), city (optional), profession (optional)
          </Alert>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{ mt: 2 }}
          >
            {uploadFile ? uploadFile.name : 'Choose File (.csv or .xlsx)'}
            <input
              type="file"
              hidden
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setUploadFile(e.target.files[0])}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUploadCSV}
            disabled={!uploadFile || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Embed Dialog */}
      <Dialog open={embedDialog} onClose={() => setEmbedDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Embed Volunteer Registration Form</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Embed Type</InputLabel>
            <Select
              value={embedSettings.type}
              label="Embed Type"
              onChange={(e) => setEmbedSettings({ ...embedSettings, type: e.target.value })}
            >
              <MenuItem value="iframe">iFrame (Inline)</MenuItem>
              <MenuItem value="popup">Popup Window</MenuItem>
              <MenuItem value="button">Button Link</MenuItem>
            </Select>
          </FormControl>
          
          {embedSettings.type === 'iframe' && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Width"
                  value={embedSettings.width}
                  onChange={(e) => setEmbedSettings({ ...embedSettings, width: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Height"
                  value={embedSettings.height}
                  onChange={(e) => setEmbedSettings({ ...embedSettings, height: e.target.value })}
                  margin="normal"
                />
              </Grid>
            </Grid>
          )}
          
          <TextField
            fullWidth
            label="Embed Code"
            value={generateEmbedCode()}
            multiline
            rows={6}
            margin="normal"
            InputProps={{ 
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy Code">
                    <IconButton 
                      onClick={() => copyToClipboard(generateEmbedCode())}
                      edge="end"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmbedDialog(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              copyToClipboard(generateEmbedCode());
              setEmbedDialog(false);
            }}
          >
            Copy Code
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VolunteerManagement;